/**
 * What: Merchant-facing copy for the Study Builder's submit readiness and the problems the Worker reports.
 * Transport / data-flow: Pure string dictionary segment spread into the study copy (study.ts) for Shopify UI and the Worker.
 */
export const studyBuilderReadinessCopy = {
    studyBuilderReadiness: {
        lifecycleBlocked: 'This test cannot be submitted right now. Check its status above and wait for any preparation to finish.',
        readyHeading: 'Ready to submit',
        readyHelp: 'Submit this setup to TestFeed.',
        /** The one blocked sentence under Submit when a real problem exists. No field is named here: the required-field stars
         * and each section's own messages carry what is wrong. */
        fixProblemsHelp: 'Fix the problems above before submitting.',
        /** The one blocked sentence under Submit when the test is merely incomplete (required fields still blank, no problem).
         * No field is named: the red `*` on each required field shows which are still empty. Submit saves first, so this never
         * mentions saving. */
        missingRequiredHelp: 'Fill in the fields marked * to submit.',
        requiredReason: 'Required before you can submit.',
        checkingHelp: 'Finishing the current check. This will only take a moment.',
        readFailureHelp: "Some details couldn't be loaded. Fix the error above, then try again.",
        submit: 'Submit',
    },
    studyBuilderProblems: {
        comparisonUnavailable: 'This test type is no longer available. Your saved options are kept. Choose an available test type or keep this draft for later.',
        unsupportedComparison: "This comparison can't be tested yet. Choose a different one.",
        chooseOneComparison: 'Choose one comparison variable.',
        onlyOneComparison: 'Only one comparison is supported per test. Add versions to the same comparison.',
        incompleteSetup: "Part of this test isn't set up correctly. Reload the page and try again.",
        questionWordingRequired: 'Question wording is required',
        comparisonRequired: 'Choose a comparison before submitting this test.',
        variantUnavailable: 'This variant is no longer available on the product. Choose another variant.',
        variantNeeded: 'Choose a variant of this product to test.',
        editRejected: "That change couldn't be applied. Try again or choose another option.",
        productCurrencyUnavailable: "We couldn't read this product's price currency. Refresh the product from Shopify and try again.",
        /** The heading of the critical "Currency unresolved" banner shown in place of price entry until a currency resolves. */
        currencyUnresolvedHeading: 'Currency unresolved',
    },
    /** What a switched-off field says it is waiting on (the Worker sets it with `disabled`). */
    studyBuilderWaiting: {
        market: 'Choose a market first.',
        currency: "Waiting for the product's price currency.",
    },
    /**
     * The one word shown in a section's working cue while an evaluation the merchant just started is in flight. The Worker
     * puts the right one on each field/action block as its `working`, so the moment a block is clicked or typed the app
     * already holds the phrase for that block's section; the app only looks the phrase up by the in-flight edit key and never
     * chooses or composes it. One phrase per area: audience fields read about shoppers, product/version about the product,
     * the comparison about the comparison. The suggestion buttons reuse the suggestions' own pending line
     * (`suggestVariationsPending028`, "Preparing test variations…") so the two never drift.
     */
    studyBuilderWorking: {
        census: 'Looking through the census data…',
        audience: 'Counting who fits your filters…',
        population: 'Rebuilding the shopper population…',
        product: 'Loading the latest product details…',
        brief: 'Updating your test setup…',
        fields: 'Setting up what you are testing…',
        questions: 'Updating your shopper questions…',
        comparison: 'Updating your comparison…',
    },
    /**
     * Stand-in content for a section with CHANGING fields while it waits on an earlier choice. The waiting section draws the
     * SAME blocks the active section does, switched off, with these general stand-in words where the real content will go — a
     * field kind, a general line, a blank picture — never an invented price, product name or value, because those do not
     * exist yet.
     */
    studyBuilderWaitingPreview: {
        // The general field kinds "What would you like to test?" lists once a variant is chosen; shown greyed while it waits.
        // Description is the highlighted example — the product description test the comparison section mirrors.
        fieldKinds: { title: 'Title', description: 'Description', price: 'Price' },
    },
    testFeedReadinessCard: {
        checkingTestfeedConnection001: 'Checking TestFeed connection',
        loadingWorkspaceStatus002: 'Loading workspace status.',
        linkThisShopifyStoreTo003: 'Link this Shopify store to a TestFeed organization before tests can be prepared.',
        connectedToTestfeed004: 'Connected to TestFeed',
        chooseDestinationProject005: 'Choose destination project',
        configurationProblem006: 'Configuration problem',
        disconnectTestfeed007: 'Disconnect TestFeed',
        managePlan008: 'Manage plan',
        testfeedConnection009: 'testfeed-connection',
        disconnectThisShopifyStoreFrom010: 'Disconnect this Shopify store from TestFeed?',
        thisDisconnectsThisShopifyStore011: 'This disconnects this Shopify store from the linked TestFeed workspace.',
        theShopifyAppStaysInstalled012: 'The Shopify app stays installed. Disconnecting does not cancel your TestFeed subscription or reset your credits.',
        disconnectingAlsoFreesThisStore017: "Disconnecting also frees this store to be connected under a different TestFeed organization. The store's remaining plan credits will go with it — they belong to this store's Shopify subscription, not to your organization.",
        newSetupSyncAndRun013: 'New setup, sync, and run actions stop until you reconnect.',
        storeDataCleanup: 'Collected store data is queued for deletion. Reconnecting collects fresh data after cleanup is confirmed.',
        cleanupPending: 'Disconnected. Store data cleanup is pending. Reconnecting is unavailable until cleanup is confirmed.',
        cleanupFailed: 'Disconnected. Store data cleanup was interrupted. You can retry cleanup now.',
        cleanupNeedsSupport: 'Disconnected. Store data cleanup needs attention. Contact support to finish cleanup.',
        cleanupUnconfirmed: 'Disconnected. Store data cleanup has not been confirmed. Refresh the status or contact support.',
        cleanupCompleted: 'Disconnected. Store data cleanup is complete. You can reconnect to collect fresh data.',
        retryCleanup: 'Retry cleanup',
        retryDisconnect: 'Retry disconnect',
        historicalTestfeedStudiesAndResults014: 'Historical TestFeed tests and results remain available under the normal retention policy.',
        keepConnected015: 'Keep connected',
        confirmDisconnect016: 'Confirm disconnect',
    },
};
//# sourceMappingURL=study-builder-readiness.js.map